import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  AddCircleOutline,
  ChevronRight,
  Replay,
  FiberNewOutlined,
  Inventory2Outlined,
  AutorenewOutlined,
  TaskAltOutlined,
  LocalShippingOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { OrderContext } from "../../contexts/OrderContext";
import OrderDetailsDialog from "./OrderDetailsDialog";
import {
  ACCENT,
  ACCENT_DARK,
  FOOTER_SPACE,
  PAGE_BG,
  OrdersList,
  sortNewestFirst,
} from "./orderUi";

const STATUS_TILES = [
  { status: "New", Icon: FiberNewOutlined },
  { status: "Issued", Icon: Inventory2Outlined },
  { status: "In Process", Icon: AutorenewOutlined },
  { status: "Completed", Icon: TaskAltOutlined },
  { status: "Delivered", Icon: LocalShippingOutlined },
];

const RECENT_COUNT = 5;

// Dashboard for customers: place/repeat an order, see where orders are.
const CustomerHome = () => {
  const navigate = useNavigate();
  const { orders = {}, fetchOrders } = useContext(OrderContext);
  const [selected, setSelected] = useState(null);

  const userName = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.name || "";
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allOrders = useMemo(
    () => sortNewestFirst(Object.values(orders).flat().filter(Boolean)),
    [orders],
  );
  const activeCount = allOrders.filter(
    (o) => o.work_status !== "Delivered",
  ).length;
  const recent = allOrders.slice(0, RECENT_COUNT);

  const handleReorder = (order) =>
    navigate("/order", { state: { reorderFrom: order } });

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100%", pb: FOOTER_SPACE }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        {/* Greeting */}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ fontSize: { xs: 12, sm: 13 } }}
        >
          Welcome back
        </Typography>
        <Typography
          fontWeight={700}
          noWrap
          sx={{ fontSize: { xs: 17, sm: 20 }, mb: { xs: 1.5, sm: 2 } }}
        >
          {userName || "Your orders"}
        </Typography>

        {/* Primary actions: side by side on phones */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "2fr 1fr" },
            gap: { xs: 1, sm: 1.5 },
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          <Paper
            elevation={0}
            onClick={() => navigate("/order")}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: "10px",
              cursor: "pointer",
              color: "#fff",
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              minWidth: 0,
            }}
          >
            <AddCircleOutline sx={{ fontSize: { xs: 24, sm: 40 } }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                fontWeight={700}
                noWrap
                sx={{ fontSize: { xs: 15, sm: 18 } }}
              >
                <Box component="span" sx={{ display: { sm: "none" } }}>
                  New order
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Place a new order
                </Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.85, display: { xs: "none", sm: "block" } }}
              >
                Photo, weight and pcs. That&apos;s it.
              </Typography>
            </Box>
            <ChevronRight sx={{ display: { xs: "none", sm: "block" } }} />
          </Paper>

          <Paper
            elevation={0}
            onClick={() => navigate("/my-orders")}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: "10px",
              cursor: "pointer",
              border: "1px solid #E4E7EC",
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              minWidth: 0,
            }}
          >
            <Replay sx={{ fontSize: { xs: 22, sm: 32 }, color: ACCENT }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                fontWeight={700}
                noWrap
                sx={{ fontSize: { xs: 15, sm: 16 } }}
              >
                <Box component="span" sx={{ display: { sm: "none" } }}>
                  Reorder
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Repeat an order
                </Box>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                From your past orders
              </Typography>
            </Box>
            <ChevronRight
              color="action"
              sx={{ display: { xs: "none", sm: "block" } }}
            />
          </Paper>
        </Box>

        {/* Status summary: one card, one row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 1,
          }}
        >
          <Typography fontWeight={700} sx={{ fontSize: { xs: 15, sm: 16 } }}>
            Order status
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {activeCount} active
          </Typography>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            borderRadius: "10px",
            overflow: "hidden",
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          {STATUS_TILES.map(({ status, Icon }, index) => {
            const count = orders[status]?.length || 0;
            return (
              <Box
                key={status}
                onClick={() =>
                  navigate(`/my-orders?status=${encodeURIComponent(status)}`)
                }
                sx={{
                  py: { xs: 1.25, sm: 1.75 },
                  px: 0.5,
                  minWidth: 0,
                  cursor: "pointer",
                  textAlign: "center",
                  borderLeft: index ? "1px solid #EEF0F3" : "none",
                  transition: "background-color 150ms ease",
                  "&:hover": { bgcolor: "#F9FAFB" },
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    color: count ? ACCENT : "#98A2B3",
                    mb: 0.5,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography
                  fontWeight={700}
                  lineHeight={1.2}
                  sx={{
                    fontSize: { xs: 18, sm: 22 },
                    color: count ? "text.primary" : "#98A2B3",
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  noWrap
                  component="div"
                  color="text.secondary"
                  sx={{ fontSize: { xs: 11, sm: 12.5 } }}
                >
                  {status}
                </Typography>
              </Box>
            );
          })}
        </Paper>

        {/* Recent orders */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography fontWeight={700} sx={{ fontSize: { xs: 15, sm: 16 } }}>
            Recent orders
          </Typography>
          {allOrders.length > RECENT_COUNT && (
            <Button
              size="small"
              endIcon={<ChevronRight />}
              onClick={() => navigate("/my-orders")}
              sx={{ color: ACCENT, fontWeight: 600, mr: -1 }}
            >
              View all
            </Button>
          )}
        </Box>
        {recent.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: "10px",
              borderStyle: "dashed",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            No orders yet. Your orders will show up here.
          </Paper>
        ) : (
          <OrdersList
            orders={recent}
            onOpen={setSelected}
            onReorder={handleReorder}
          />
        )}
      </Box>

      <OrderDetailsDialog
        order={selected}
        onClose={() => setSelected(null)}
        onReorder={handleReorder}
      />
    </Box>
  );
};

export default CustomerHome;
